// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook for fetching and managing individual asset data.
 * This module provides a convenient way to request and cache
 * asset information from the remote API using React Query.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { detailedAssetSchema } from '@/asset/api/types';
import { ONE_MINUTE_MS } from '@/utilities/time-constants';
import { useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * Schema for validating the API response when fetching an asset.
 * Transforms the response to provide a more intuitive structure with
 * the asset data separated from metadata.
 */
export const assetResponseSchema = z.object({
  data: detailedAssetSchema,
  metadata: responseMetadataSchema,
});

/**
 * A React hook that fetches and caches asset data from the remote API.
 * Uses React Query's suspense mode for loading states and automatic
 * background revalidation. The hook will suspend the component while
 * the initial data is being fetched.
 *
 * @param assetKey - Unique identifier of the asset to fetch
 * @returns Object containing the asset data and query information
 * @property {Asset} asset - The fetched asset data
 * @property {boolean} isLoading - Whether a background refresh is in progress
 * @property {boolean} isError - Whether the last fetch attempt resulted in an error
 * @property {function} refetch - Function to manually trigger a refresh
 * @throws Will throw a promise during initial load (for Suspense)
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export function useAsset(assetKey: string) {
  const { data, ...query } = useSuspenseQuery({
    queryKey: ['asset', assetKey],
    queryFn: async () => {
      const url = `/v1/asset/${assetKey}`;
      const response = await api.get(url);
      const result = await parseResponse(assetResponseSchema, response.data, {
        schemaName: 'Asset',
        endpoint: url,
      });

      return result.data;
    },
    refetchInterval: ONE_MINUTE_MS,
  });

  return { asset: data, ...query };
}
